import { motion } from "framer-motion";
import { 
  Phone, 
  FileText, 
  Shield, 
  AlertTriangle,
  ExternalLink,
  HelpCircle,
  CheckCircle,
  ArrowRight,
  Building,
  Globe
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const KnowledgeHub = () => {
  const guides = [
    {
      id: "fir",
      title: "How to File an FIR",
      icon: FileText,
      color: "#2c3e50",
      steps: [
        "Visit your nearest Police Station or Cyber Cell",
        "Bring all evidence: bank statements, screenshots, call records",
        "Provide details: scammer's phone number, bank account, transaction IDs",
        "Get a copy of the FIR with the FIR number",
        "Keep the FIR number safe for future reference"
      ],
      tip: "You can also file an online complaint at cybercrime.gov.in before visiting the police station."
    },
    {
      id: "helpline",
      title: "What is the 1930 Helpline?",
      icon: Phone,
      color: "#e74c3c",
      description: "1930 is the National Cyber Crime Helpline by the Government of India. It's available 24/7 and free to call.",
      points: [
        "Report cyber fraud immediately after it happens",
        "The faster you report, the higher chance of freezing the fraudster's account",
        "Keep your bank details and transaction info ready",
        "You'll get an acknowledgement number for tracking"
      ],
      tip: "Call 1930 within the first few hours of the fraud for best results."
    },
    {
      id: "rbi",
      title: "RBI Guidelines for Fraud Victims",
      icon: Building,
      color: "#27ae60",
      points: [
        "Report unauthorized transactions to your bank within 3 days",
        "If you report within 3 days, your liability is zero for proven fraud",
        "Banks must credit the disputed amount within 10 days while investigating",
        "If bank fails to act, you can complain to RBI Banking Ombudsman"
      ],
      links: [
        { text: "RBI Ombudsman Portal", url: "https://cms.rbi.org.in" },
        { text: "RBI Customer Education", url: "https://www.rbi.org.in/commonman/English/Scripts/AgainstBank.aspx" }
      ]
    }
  ];

  const faqs = [
    {
      question: "What should I do immediately after being scammed?",
      answer: "1. Call 1930 immediately to report the fraud. 2. Call your bank to block your account/card. 3. Save all evidence (screenshots, call records, messages). 4. Visit the police station to file an FIR. 5. Report on our platform to track your case."
    },
    {
      question: "Can I get my money back?",
      answer: "Recovery depends on how quickly you report. The faster you act (within hours), the higher the chances. If the money hasn't left the fraudster's account, it can be frozen and recovered. According to RBI guidelines, if you report within 3 days and the fraud is proven, your bank must refund the amount."
    },
    {
      question: "What evidence should I collect?",
      answer: "Collect everything: Bank statements showing the fraudulent transaction, screenshots of messages/calls from the scammer, the scammer's phone number or email, any links or websites they shared, UPI transaction IDs, and any other communication records."
    },
    {
      question: "Is PEFF a government website?",
      answer: "No, PEFF is a private initiative to help senior citizens organize their fraud complaints and track status. For official complaints, please visit cybercrime.gov.in or call 1930. We assist in organizing your data but are not affiliated with any government body."
    },
    {
      question: "How long does it take to resolve a case?",
      answer: "Cyber fraud cases typically take 3-6 months for investigation. Recovery depends on whether the money can be traced and frozen. Simple cases with quick reporting may see results in weeks, while complex cases involving multiple jurisdictions take longer."
    },
    {
      question: "What is the difference between FIR and Cyber Crime complaint?",
      answer: "An FIR (First Information Report) is filed at your local police station and is mandatory for prosecution. A Cyber Crime complaint (at cybercrime.gov.in) is an online acknowledgement that helps in initial investigation. Both are important - file the online complaint first, then follow up with an FIR."
    }
  ];

  const importantContacts = [
    { name: "National Cyber Crime Helpline", number: "1930", available: "24/7" },
    { name: "Women Helpline", number: "1091", available: "24/7" },
    { name: "RBI Helpline", number: "14440", available: "9 AM - 5 PM" },
    { name: "Senior Citizen Helpline", number: "14567", available: "8 AM - 8 PM" },
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8 md:py-12" data-testid="knowledge-hub-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
            Help Guide
          </h1>
          <p className="text-lg text-[#546e7a] max-w-2xl mx-auto">
            Simple guides to help you understand your rights and take action against financial fraud
          </p>
        </motion.div>

        {/* Emergency Contacts Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-[#e74c3c] text-white mb-8" data-testid="emergency-contacts">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Phone className="w-6 h-6" />
                Emergency Helpline Numbers
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {importantContacts.map((contact) => (
                  <a
                    key={contact.number}
                    href={`tel:${contact.number}`}
                    className="bg-white/10 rounded-lg p-4 hover:bg-white/20 transition-colors"
                    data-testid={`contact-${contact.number}`}
                  >
                    <p className="text-2xl font-bold">{contact.number}</p>
                    <p className="text-sm opacity-90">{contact.name}</p>
                    <p className="text-xs opacity-75">{contact.available}</p>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Guides Section */}
        <div className="space-y-6 mb-12">
          {guides.map((guide, index) => (
            <motion.div
              key={guide.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * (index + 1) }}
            >
              <Card className="shadow-lg border-l-4" style={{ borderLeftColor: guide.color }} data-testid={`guide-${guide.id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] flex items-center gap-3">
                    <div className="p-3 rounded-full" style={{ backgroundColor: `${guide.color}15` }}>
                      <guide.icon className="w-6 h-6" style={{ color: guide.color }} />
                    </div>
                    {guide.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {guide.description && (
                    <p className="text-lg text-[#546e7a] mb-4">{guide.description}</p>
                  )}
                  
                  {guide.steps && (
                    <div className="space-y-3 mb-4">
                      {guide.steps.map((step, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[#2c3e50] font-bold flex-shrink-0">
                            {i + 1}
                          </span>
                          <p className="text-base text-[#2c3e50] pt-1">{step}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {guide.points && (
                    <ul className="space-y-2 mb-4">
                      {guide.points.map((point, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-[#27ae60] flex-shrink-0 mt-0.5" />
                          <p className="text-base text-[#2c3e50]">{point}</p>
                        </li>
                      ))}
                    </ul>
                  )}

                  {guide.tip && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-[#f39c12] flex-shrink-0 mt-0.5" />
                      <p className="text-base text-[#2c3e50]">
                        <strong>Tip:</strong> {guide.tip}
                      </p>
                    </div>
                  )}

                  {guide.links && (
                    <div className="mt-4 flex flex-wrap gap-3">
                      {guide.links.map((link) => (
                        <a
                          key={link.url}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-[#2c3e50] text-white px-4 py-2 rounded-lg hover:bg-[#1a252f] transition-colors"
                        >
                          <Globe className="w-4 h-4" />
                          {link.text}
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* FAQs Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="shadow-lg" data-testid="faq-section">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] flex items-center gap-3">
                <HelpCircle className="w-7 h-7" />
                Frequently Asked Questions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`faq-${index}`} data-testid={`faq-item-${index}`}>
                    <AccordionTrigger className="text-left text-base font-semibold text-[#2c3e50] hover:text-[#e74c3c] py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-base text-[#546e7a] leading-relaxed pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </motion.div>

        {/* Official Resources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8"
        >
          <Card className="bg-slate-50" data-testid="official-resources">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-[#2c3e50] font-['Manrope'] mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6" />
                Official Government Resources
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <a
                  href="https://cybercrime.gov.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
                >
                  <div>
                    <p className="text-base font-semibold text-[#2c3e50]">National Cyber Crime Portal</p>
                    <p className="text-sm text-[#546e7a]">cybercrime.gov.in</p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-[#546e7a]" />
                </a>
                <a
                  href="https://cms.rbi.org.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
                >
                  <div>
                    <p className="text-base font-semibold text-[#2c3e50]">RBI Banking Ombudsman</p>
                    <p className="text-sm text-[#546e7a]">cms.rbi.org.in</p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-[#546e7a]" />
                </a>
                <a
                  href="https://sachet.rbi.org.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
                >
                  <div>
                    <p className="text-base font-semibold text-[#2c3e50]">RBI Sachet Portal</p>
                    <p className="text-sm text-[#546e7a]">Report illegal financial schemes</p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-[#546e7a]" />
                </a>
                <a
                  href="https://consumerhelpline.gov.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
                >
                  <div>
                    <p className="text-base font-semibold text-[#2c3e50]">Consumer Helpline</p>
                    <p className="text-sm text-[#546e7a]">consumerhelpline.gov.in</p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-[#546e7a]" />
                </a>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default KnowledgeHub;
